import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import styled from 'styled-components';
import { ZoomButtonWrapper } from '../../productConfig/productConfigStyles';
import ProtectedImage, {
  useImageIntentFriction,
  useProtectedZoomContent,
} from '../protectedImage';
import useImageIntentZoomContent from '../protectedImage/imageIntentZoomContent';

const ImgContainer = styled.div`
  flex: 1 0 calc(25% - 24px); // 25% for 4-across grid minus gutter
  max-width: calc(25% - 24px); // Ensure 4-across grid always
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    flex: 0 1 calc(50% - 14px);
    max-width: calc(50% - 14px);
  }

  img {
    max-width: 100%;
    height: auto;
    background-color: white;
    box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    -webkit-user-drag: none;
    user-select: none;
    object-fit: cover;
  }
`;

const Name = styled.div`
  text-align: center;
  margin-top: 10px;
`;

const PoseItem = ({
  onClick,
  name,
  selected = false,
  disabled = false,
  intentGallery,
  intentSurface = 'pose_picker',
  registerIntentSaveShortcut = true,
  ...imageProps
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  // Registers Cmd/Ctrl+S for the picker unless the caller registers its own;
  // the tile's own handlers are on ProtectedImage below.
  useImageIntentFriction(
    registerIntentSaveShortcut ? { surface: intentSurface } : undefined,
  );
  // intentGallery: a pooled sibling pose's own gallery; otherwise the hook's
  // Redux defaults (the order's gallery) apply.
  const frictionContext = {
    surface: intentSurface,
    imageKey: imageProps.src,
    ...intentGallery,
  };
  // The zoomed pose renders in a portal outside this tree; it gets its own
  // friction props (surface `pose_picker_zoom`) and, when the studio has
  // protection on, its own overlay.
  const poseZoomContent = useImageIntentZoomContent(frictionContext);
  const ZoomContent = useProtectedZoomContent(poseZoomContent);
  const handleZoomChange = useCallback((shouldZoom) => {
    setIsZoomed(shouldZoom);
  }, []);

  const handleZoomClick = (e) => {
    e.stopPropagation();
    setIsZoomed((zoomed) => !zoomed);
  };

  return (
    <ImgContainer onContextMenu={(event) => event.preventDefault()}>
      <ControlledZoom
        isZoomed={isZoomed}
        onZoomChange={handleZoomChange}
        ZoomContent={ZoomContent}
      >
        <button
          type="button"
          aria-label={`Select ${name}`}
          aria-pressed={selected}
          disabled={disabled}
          onClick={onClick}
          // Block and full-width, like the div this replaced: a <button>
          // shrink-wraps its content, so a preview that fails to load would
          // leave a zero-width target that cannot select the pose.
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: 0,
            background: 'transparent',
            textAlign: 'inherit',
          }}
        >
          {/* The friction handlers sit on ProtectedImage's wrapper, not the
              <img>: ImgContainer gives the img `pointer-events: none`, so a
              real right-click or long-press targets the wrapper (or, with
              protection on, the save shield above the image). */}
          <ProtectedImage
            surface={frictionContext.surface}
            imageKey={frictionContext.imageKey}
            intentGallery={intentGallery}
            imageSrc={imageProps.src}
          >
            <img
              {...imageProps}
              alt={name || imageProps.alt || ''}
              onContextMenu={(e) => e.preventDefault()}
            />
          </ProtectedImage>
        </button>
        <ZoomButtonWrapper
          as="button"
          type="button"
          aria-label={`Zoom ${name}`}
          onClick={handleZoomClick}
        >
          <MagnifyingGlassPlusIcon />
        </ZoomButtonWrapper>
        {name && <Name>{name}</Name>}
      </ControlledZoom>
    </ImgContainer>
  );
};

PoseItem.propTypes = {
  onClick: PropTypes.func,
  name: PropTypes.string,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  intentSurface: PropTypes.string,
  registerIntentSaveShortcut: PropTypes.bool,
  intentGallery: PropTypes.shape({
    galleryKey: PropTypes.string,
    picsGalleryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dp2GalleryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

PoseItem.defaultProps = {
  onClick: null,
  name: '',
  intentGallery: {},
};

export default PoseItem;
